import type { ContextComposition } from "../api/hooks/useStatefulAgents";
import { formatTokens } from "../utils/formatTokens";
import styles from "./RunView.module.css";

// The server's `ContextCategory`, in the order it lists them: what is fixed for the
// run first, then what the conversation brings in.
const CATEGORY_LABEL: [string, string][] = [
  ["agent_instructions", "Agent instructions"],
  ["shared_prompts", "Shared prompts"],
  ["platform_guidance", "Platform guidance"],
  ["tool_catalogue", "Tool catalogue"],
  ["project_context", "Project context"],
  ["agent_state", "Agent state"],
  ["operator_messages", "Operator messages and turn notes"],
  ["open_items", "Open items"],
  ["read_documents", "Read documents"],
  ["read_emails", "Read emails and messages"],
  ["specialist_replies", "Specialist replies"],
  ["other_tool_results", "Other tool results"],
  ["agent_output", "The agent's own output"],
  ["compacted_history", "Compacted history"],
];

const pct = (part: number, whole: number): string => {
  if (whole <= 0) return "";
  const rounded = Math.round((100 * part) / whole);
  return rounded === 0 && part > 0 ? "<1%" : `${rounded}%`;
};

// What fills one agent's window, by category, with each tool-result category split
// by tool. Approximate: measured by size and scaled to the call's input tokens.
export const ContextBreakdown = ({
  used,
  composition,
}: {
  used: number;
  composition: ContextComposition;
}) => {
  const known = new Set(CATEGORY_LABEL.map(([key]) => key));
  const rows: [string, string][] = [
    ...CATEGORY_LABEL,
    // A category this build does not know yet still shows, under its own name.
    ...Object.keys(composition.categories)
      .filter((key) => !known.has(key))
      .map((key): [string, string] => [key, key]),
  ];
  return (
    <div className={styles.contextBreakdown}>
      <table>
        <tbody>
          {rows
            .filter(([key]) => (composition.categories[key] ?? 0) > 0)
            .map(([key, label]) => {
              const tokens = composition.categories[key];
              const tools = Object.entries(composition.tools?.[key] ?? {})
                .filter(([, n]) => n > 0)
                .sort(([, a], [, b]) => b - a);
              return [
                <tr key={key}>
                  <td>{label}</td>
                  <td className={styles.contextBreakdownFigure}>{formatTokens(tokens)}</td>
                  <td className={styles.contextBreakdownFigure}>{pct(tokens, used)}</td>
                </tr>,
                ...tools.map(([tool, n]) => (
                  <tr key={`${key}/${tool}`} className={styles.contextBreakdownTool}>
                    <td>{tool}</td>
                    <td className={styles.contextBreakdownFigure}>{formatTokens(n)}</td>
                    <td className={styles.contextBreakdownFigure}>{pct(n, used)}</td>
                  </tr>
                )),
              ];
            })}
        </tbody>
      </table>
      <p className={styles.contextBreakdownNote}>
        Approximate: each part measured by its size and scaled to the tokens the
        model reported.
      </p>
    </div>
  );
};
